import { cva, type VariantProps } from 'class-variance-authority';
import type { HTMLAttributes, ReactNode } from 'react';
import { cn } from './cn.js';

export type StatusTone = 'neutral' | 'accent' | 'success' | 'warning' | 'danger' | 'processing';

const statusPillVariants = cva(
  'inline-flex min-h-7 items-center gap-1.5 rounded-full border px-2.5 py-1 font-mono text-xs font-medium leading-none tracking-[0.02em]',
  {
    variants: {
      tone: {
        neutral: 'border-border bg-surface text-ink-3',
        accent: 'border-accent-line bg-accent-soft text-accent',
        success:
          'border-[color-mix(in_oklch,var(--ok)_40%,var(--border))] bg-[color-mix(in_oklch,var(--ok)_12%,transparent)] text-ok',
        warning:
          'border-[color-mix(in_oklch,var(--warn)_40%,var(--border))] bg-[color-mix(in_oklch,var(--warn)_12%,transparent)] text-warn',
        danger:
          'border-[color-mix(in_oklch,var(--err)_40%,var(--border))] bg-[color-mix(in_oklch,var(--err)_12%,transparent)] text-err',
        processing: 'border-accent-line bg-accent-soft text-accent',
      },
    },
    defaultVariants: { tone: 'neutral' },
  },
);

const successful = new Set(['active', 'available', 'complete', 'completed', 'ready', 'succeeded']);
const warning = new Set(['action_required', 'partially_failed', 'retryable']);
const dangerous = new Set(['error', 'failed', 'invalid']);
const processing = new Set([
  'accepted',
  'delivering',
  'expanding',
  'in_progress',
  'materializing',
  'pending',
  'queued',
  'running',
  'syncing',
]);

export function statusTone(status: string): StatusTone {
  const normalized = status.trim().toLowerCase();
  if (successful.has(normalized)) return 'success';
  if (warning.has(normalized)) return 'warning';
  if (dangerous.has(normalized)) return 'danger';
  if (processing.has(normalized)) return 'processing';
  return 'neutral';
}

export interface StatusPillProps
  extends
    Omit<HTMLAttributes<HTMLSpanElement>, 'children'>,
    VariantProps<typeof statusPillVariants> {
  children?: ReactNode;
  status: string;
}

export function StatusPill({ children, className, status, tone, ...props }: StatusPillProps) {
  const resolvedTone = tone ?? statusTone(status);
  return (
    <span className={cn(statusPillVariants({ tone: resolvedTone }), className)} {...props}>
      <span
        aria-hidden="true"
        className={cn(
          'size-1.5 rounded-full bg-current',
          resolvedTone === 'processing' && 'animate-[sc-pulse_1.4s_ease-in-out_infinite]',
        )}
      />
      {children ?? status.replaceAll('_', ' ')}
    </span>
  );
}
